import { lazy, Suspense, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router";
import { Building2, Coins, Hammer, Map } from "lucide-react";

import {
  useWorkAdvisoryHistory,
  useWorkAdvisoryReport,
} from "@/hooks/queries/useWorkAdvisoryReport";
import { formatMoneyCompact, formatNumber } from "@/lib/format";
import { formatPeriod } from "@/lib/workadvisory-period";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { DEMO_COMPANY_NAME } from "@/lib/workadvisory-demo";
import { WorkAdvisoryFunnel } from "@/components/workadvisory/WorkAdvisoryFunnel";
import { WorksTable } from "@/components/workadvisory/WorksTable";
import type { WorkAdvisoryMetric } from "@/types/workadvisory";

// El mapa trae los paths del país: fuera del bundle principal.
const WorkAdvisoryMap = lazy(() =>
  import("@/components/workadvisory/WorkAdvisoryMap").then((m) => ({
    default: m.WorkAdvisoryMap,
  })),
);

function Card({
  title,
  icon,
  aside,
  children,
  className = "",
}: {
  title: string;
  icon: React.ReactNode;
  aside?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-2xl bg-card p-5 shadow-sm ${className}`}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{icon}</span>
          <h3 className="text-sm font-bold">{title}</h3>
        </div>
        {aside}
      </div>
      {children}
    </div>
  );
}

function Kpi({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="rounded-2xl bg-card p-5 shadow-sm">
      <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-3xl font-extrabold tabular-nums">{value}</p>
      <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

/** Qué miden el mapa y el ranking. Vive dentro de su tarjeta: no afecta a nada más. */
function MetricToggle({
  metric,
  onChange,
}: {
  metric: WorkAdvisoryMetric;
  onChange: (metric: WorkAdvisoryMetric) => void;
}) {
  const option = (
    value: WorkAdvisoryMetric,
    label: string,
    Icon: typeof Hammer,
  ) => (
    <button
      onClick={() => onChange(value)}
      className={cn(
        "flex items-center gap-1.5 rounded-full px-3 py-1.5 transition-colors",
        metric === value
          ? "bg-primary text-primary-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      <Icon className="size-3.5" />
      {label}
    </button>
  );

  return (
    <div className="flex items-center rounded-full border bg-background p-0.5 text-xs font-semibold">
      {option("obras", "Obras", Hammer)}
      {option("amount", "Inversión", Coins)}
    </div>
  );
}

/**
 * Las dos acciones del informe. Todavía no hacen nada: el formato del informe se
 * está conversando con la empresa, y hasta entonces avisan en vez de fallar.
 */
function ReportActions() {
  const pending = (what: string) => () =>
    toast.info(`${what} llega con la próxima entrega del informe.`);

  return (
    <div className="flex w-full flex-col gap-2 sm:w-auto">
      <Button
        onClick={pending("La descarga en PDF")}
        className="gap-2 font-bold"
      >
        <span className="size-2 rounded-[2px] bg-amber-400" />
        Descargar informe en PDF
      </Button>
      <Button
        variant="outline"
        onClick={pending("El envío al equipo")}
        className="font-bold text-primary"
      >
        Enviar al equipo
      </Button>
    </div>
  );
}

/** Cambia de período. Solo aparece cuando ya hay más de un informe publicado. */
function PeriodPicker({ currentId }: { currentId?: number }) {
  const navigate = useNavigate();
  const { data: history } = useWorkAdvisoryHistory();

  if (!history || history.length < 2) return null;

  return (
    <select
      className="rounded-full border bg-background px-3 py-1.5 text-xs font-semibold"
      value={currentId ?? history[0]!.workadvisoryreport_id}
      onChange={(event) =>
        navigate(
          Number(event.target.value) === history[0]!.workadvisoryreport_id
            ? "/work-advisory"
            : `/work-advisory/${event.target.value}`,
        )
      }
    >
      {history.map((report, index) => (
        <option
          key={report.workadvisoryreport_id}
          value={report.workadvisoryreport_id}
        >
          {formatPeriod(
            report.workadvisoryreport_periodstart,
            report.workadvisoryreport_periodend,
          )}
          {index === 0 ? " · último" : ""}
        </option>
      ))}
    </select>
  );
}

function EmptyState({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="mx-auto flex w-full max-w-lg flex-1 flex-col items-center justify-center px-4 py-24 text-center">
      <h1 className="text-2xl font-extrabold tracking-tight">{title}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{hint}</p>
    </div>
  );
}

export default function WorkAdvisory() {
  const [metric, setMetric] = useState<WorkAdvisoryMetric>("obras");
  const { id } = useParams();
  const reportId = id ? Number(id) : undefined;

  const { data: report, isLoading, isError } = useWorkAdvisoryReport(reportId);
  const stats = report?.workadvisoryreport_data;
  const criteria = report?.workadvisoryreport_criteria;

  // El monto en juego sale de las obras del informe: la foto ya las trae enteras,
  // así que no hay nada que volver a consultar. Con monto 0 en SEACE cuenta el que
  // declaran las bases —el mismo que se muestra en la fila—, o el total de arriba
  // contradice lo que se lee abajo.
  const totals = useMemo(() => {
    const works = stats?.works ?? [];
    return {
      obras: works.length,
      amount: works.reduce(
        (sum, work) => sum + (work.amount || work.declaredAmount || 0),
        0,
      ),
    };
  }, [stats]);

  // Ordenado por la métrica activa: un ranking cuyos números no van de mayor a
  // menor se lee como un error.
  const ranking = useMemo(
    () =>
      [...(stats?.byDepartment ?? [])].sort((a, b) =>
        metric === "amount" ? b.amount - a.amount : b.obras - a.obras,
      ),
    [stats, metric],
  );
  const maxValue = ranking.reduce(
    (max, row) => Math.max(max, metric === "amount" ? row.amount : row.obras),
    0,
  );
  const format = (value: number) =>
    metric === "amount" ? formatMoneyCompact(value) : formatNumber(value);

  // Quién entra lo deciden `PlanGuard` y el gate del backend; acá solo queda el
  // fallo de la consulta.
  if (isError) {
    return (
      <EmptyState
        title="No pudimos cargar el informe"
        hint="Vuelve a intentarlo en un momento."
      />
    );
  }

  // El informe se publica a mano, así que entre el lunes que se genera y el día
  // que se revisa no hay nada que mostrar.
  if (!isLoading && !report) {
    return (
      <EmptyState
        title="Todavía no hay informes publicados"
        hint="El próximo informe llega apenas se revise el período en curso."
      />
    );
  }

  return (
    <div className="mx-auto w-full flex-1 space-y-6 px-4 py-6 md:px-14 max-w-7xl">
      <div className="flex flex-wrap items-start justify-between gap-6 rounded-2xl bg-card p-6 shadow-sm">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-[11px] font-bold uppercase tracking-wide text-primary">
              Informe semanal
              {report &&
                ` · ${formatPeriod(
                  report.workadvisoryreport_periodstart,
                  report.workadvisoryreport_periodend,
                ).toUpperCase()}`}
            </p>
            <PeriodPicker currentId={reportId} />
          </div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">
            Obras de la semana para {DEMO_COMPANY_NAME}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Las obras convocadas esta semana que entran en el rango de la empresa
            {criteria && (
              <>
                {" "}
                <span className="font-semibold text-foreground/80">
                  ({formatMoneyCompact(criteria.amountMin)} a{" "}
                  {formatMoneyCompact(criteria.amountMax)})
                </span>
              </>
            )}
            . Abajo el listado completo, de mayor a menor valor referencial.
          </p>
        </div>

        <ReportActions />
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        {isLoading ? (
          <>
            <Skeleton className="h-28 rounded-2xl" />
            <Skeleton className="h-28 rounded-2xl" />
            <Skeleton className="h-28 rounded-2xl" />
          </>
        ) : (
          <>
            <Kpi
              label="Obras"
              value={formatNumber(totals.obras)}
              hint="Seleccionadas para ustedes esta semana"
            />
            <Kpi
              label="Monto en juego"
              value={formatMoneyCompact(totals.amount)}
              hint="Suma de los valores referenciales"
            />
            <Kpi
              label="Departamentos"
              value={formatNumber(ranking.length)}
              hint="Donde están las obras que les recomendamos"
            />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card
          title="Dónde están las obras que les recomendamos"
          icon={<Map className="size-4" />}
          className="lg:col-span-2"
          aside={
            <div className="flex items-center gap-3">
              <MetricToggle metric={metric} onChange={setMetric} />
              <span className="hidden text-[10px] font-semibold uppercase tracking-wide text-muted-foreground sm:inline">
                {formatNumber(totals.obras)} obras · {ranking.length}{" "}
                dptos
              </span>
            </div>
          }
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Suspense fallback={<Skeleton className="h-[520px] rounded-xl" />}>
              <WorkAdvisoryMap byDepartment={ranking} metric={metric} />
            </Suspense>
            {isLoading ? (
              <Skeleton className="h-64 self-center rounded-xl" />
            ) : (
              <ol className="space-y-2.5 self-center">
                {ranking.slice(0, 8).map((row) => {
                  const value = metric === "amount" ? row.amount : row.obras;
                  const width = maxValue > 0 ? (value / maxValue) * 100 : 0;
                  return (
                    <li key={row.department}>
                      <div className="flex items-baseline justify-between gap-2 text-xs">
                        <span className="truncate font-semibold capitalize">
                          {row.department.toLowerCase()}
                        </span>
                        <span className="shrink-0 tabular-nums text-muted-foreground">
                          {format(value)}
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-[var(--chart-1)]"
                          style={{ width: `${Math.max(width, 1)}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </Card>

        <Card
          title="Cuántas sobreviven a cada filtro"
          icon={<Building2 className="size-4" />}
        >
          {isLoading ? (
            <Skeleton className="h-64 rounded-xl" />
          ) : (
            <WorkAdvisoryFunnel steps={stats?.funnel ?? []} />
          )}
        </Card>
      </div>

      <Card title="Listado de obras" icon={<Hammer className="size-4" />}>
        {isLoading ? (
          <Skeleton className="h-96 rounded-xl" />
        ) : (
          <WorksTable works={stats?.works ?? []} />
        )}
      </Card>
    </div>
  );
}
